import { useEffect, useMemo, useState } from 'react';
import { Layers, Plus, ArrowUpRight, CalendarClock, Undo2 } from 'lucide-react';
import { Card } from './Card';
import { Button } from './Button';
import { Badge } from './Badge';
import { Alert } from './Alert';
import { Input } from './Input';
import { Select } from './Select';
import { Loader } from './Loader';
import { Dialog } from './Dialog';
import { EmbeddedPayment } from './EmbeddedPayment';
import { api } from '../services/api';
import { BillingSubscription, ChangeKind, ChangeQuote, ChangeResult, PriceInterval } from '../types';

const SUB_STATUS: Record<string, { label: string; tone: 'success' | 'warning' | 'danger' | 'accent' }> = {
  trialing: { label: 'Trial', tone: 'accent' },
  active: { label: 'Active', tone: 'success' },
  past_due: { label: 'Payment due', tone: 'warning' },
  suspended: { label: 'Suspended', tone: 'danger' },
};

const PER: Record<PriceInterval, string> = { monthly: 'month', quarterly: 'quarter', yearly: 'year' };
const INTERVAL_NAME: Record<PriceInterval, string> = { monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };

/** What one unit is called, per product. */
const UNIT: Record<string, [string, string]> = { hosting: ['website', 'websites'] };

function units(product: string, n: number): string {
  const [one, many] = UNIT[product] || ['unit', 'units'];
  return `${n} ${n === 1 ? one : many}`;
}

function money(minor: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
  }).format(minor / 100);
}

function day(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

type Action = { sub: BillingSubscription; mode: 'add' | 'upgrade' | 'schedule' };

export function SubscriptionsCard({
  orgId,
  subscriptions,
  canChange,
  onChanged,
  openAddFor,
  returnTo,
}: {
  orgId: string;
  subscriptions: BillingSubscription[];
  canChange: boolean;
  onChanged: (message: string) => Promise<void>;
  /** Subscription to open "add units" on, when arriving from a product. */
  openAddFor?: string | null;
  /** Where to offer to go back to, when arriving from a product. */
  returnTo?: string | null;
}) {
  const [action, setAction] = useState<Action | null>(null);
  const [error, setError] = useState<string | null>(null);
  //: Only ever acted on once: reopening the dialog after the customer closed it
  //: would trap them on the page, since the query parameter is still in the URL.
  const [deepLinkUsed, setDeepLinkUsed] = useState(false);

  useEffect(() => {
    if (deepLinkUsed || !openAddFor || !canChange) return;
    const sub = subscriptions.find((s) => s.id === openAddFor);
    if (!sub) return;
    setDeepLinkUsed(true);
    if (sub.changeable) setAction({ sub, mode: 'add' });
  }, [openAddFor, subscriptions, canChange, deepLinkUsed]);

  const clear = async (sub: BillingSubscription) => {
    try {
      await api.billing.clearSchedule(orgId, sub.id);
      await onChanged('The planned change was removed.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the planned change');
    }
  };

  return (
    <Card padding="var(--space-6)">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Layers size={20} color="var(--accent)" />
        <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>Subscriptions</h2>
      </div>
      {error && (
        <div style={{ marginBottom: 12 }}>
          <Alert tone="danger" title="Error">
            {error}
          </Alert>
        </div>
      )}
      {subscriptions.length === 0 ? (
        <div
          style={{
            padding: 'var(--space-6)',
            background: 'var(--bg-inset)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-subtle)',
            textAlign: 'center',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-tertiary)',
          }}
        >
          No subscription yet. Choose a plan from the Products page.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {subscriptions.map((sub) => {
            const status = SUB_STATUS[sub.status];
            const planned = sub.scheduled_quantity !== null || sub.scheduled_moves.length > 0;
            const upgradable = sub.targets.some(
              (t) =>
                t.plan_code !== sub.plan_code &&
                (t.prices[sub.interval] ?? 0) > sub.unit_amount_minor,
            );
            return (
              <div
                key={sub.id}
                style={{
                  padding: '12px 14px',
                  background: 'var(--bg-inset)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)',
                  fontSize: 'var(--text-sm)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                      {sub.product_name} {sub.plan_name}
                      {sub.quantity > 1 || UNIT[sub.product_code] ? (
                        <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                          {' '}
                          · {units(sub.product_code, sub.quantity)}
                        </span>
                      ) : null}
                    </div>
                    <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', marginTop: 2 }}>
                      {sub.comped
                        ? 'Included by CYRN, nothing to pay'
                        : sub.is_free
                        ? 'Free plan'
                        : `${money(sub.unit_amount_minor * sub.quantity, sub.currency)} / ${PER[sub.interval]}${
                            sub.quantity > 1 ? ` (${money(sub.unit_amount_minor, sub.currency)} each)` : ''
                          } · ${sub.cancel_at_period_end ? 'ends' : 'renews'} ${day(sub.current_period_end)}`}
                    </div>
                    {sub.discount && (
                      <div style={{ color: 'var(--success)', fontSize: 'var(--text-xs)', marginTop: 2 }}>
                        {sub.discount.label || 'Discount'}: −{sub.discount.rate}
                        {sub.discount.periods_remaining !== null
                          ? ` on the next ${sub.discount.periods_remaining > 1 ? `${sub.discount.periods_remaining} invoices` : 'invoice'}`
                          : ''}
                        {sub.discount.ends_at ? ` until ${day(sub.discount.ends_at)}` : ''}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {sub.comped && <Badge tone="accent">Included</Badge>}
                    {status && (
                      <Badge tone={status.tone} dot>
                        {status.label}
                      </Badge>
                    )}
                  </div>
                </div>
                {planned && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                      color: 'var(--text-secondary)',
                      fontSize: 'var(--text-xs)',
                    }}
                  >
                    <span>
                      From {day(sub.current_period_end)}:{' '}
                      {[
                        (sub.scheduled_quantity ?? sub.quantity) > 0
                          ? `${units(sub.product_code, sub.scheduled_quantity ?? sub.quantity)} on ${sub.plan_name}`
                          : null,
                        ...sub.scheduled_moves.map(
                          (m) =>
                            `${units(sub.product_code, m.quantity)} on ${m.plan_name}${
                              m.interval !== sub.interval ? ` (${INTERVAL_NAME[m.interval].toLowerCase()})` : ''
                            }`,
                        ),
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                    {canChange && (
                      <Button size="sm" variant="ghost" icon={<Undo2 size={14} />} onClick={() => clear(sub)}>
                        Undo
                      </Button>
                    )}
                  </div>
                )}
                {canChange && sub.changeable && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button size="sm" variant="secondary" icon={<Plus size={14} />} onClick={() => setAction({ sub, mode: 'add' })}>
                      Add {UNIT[sub.product_code]?.[1] || 'units'}
                    </Button>
                    {upgradable && (
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={<ArrowUpRight size={14} />}
                        onClick={() => setAction({ sub, mode: 'upgrade' })}
                      >
                        Upgrade
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<CalendarClock size={14} />}
                      onClick={() => setAction({ sub, mode: 'schedule' })}
                    >
                      Change at renewal
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {action && action.mode !== 'schedule' && (
        <ChangeNowDialog
          orgId={orgId}
          sub={action.sub}
          kind={action.mode === 'add' ? 'add_units' : 'upgrade'}
          returnTo={returnTo ?? null}
          onClose={() => setAction(null)}
          onDone={async (m) => {
            setAction(null);
            await onChanged(m);
          }}
        />
      )}
      {action && action.mode === 'schedule' && (
        <ScheduleDialog
          orgId={orgId}
          sub={action.sub}
          onClose={() => setAction(null)}
          onDone={async (m) => {
            setAction(null);
            await onChanged(m);
          }}
        />
      )}
    </Card>
  );
}

/** Add units or move some to a higher plan now, paying the prorated difference. */
function ChangeNowDialog({
  orgId,
  sub,
  kind,
  onClose,
  onDone,
  returnTo,
}: {
  orgId: string;
  sub: BillingSubscription;
  kind: ChangeKind;
  onClose: () => void;
  onDone: (message: string) => Promise<void>;
  returnTo?: string | null;
}) {
  const higher = useMemo(
    () =>
      sub.targets.filter(
        (t) => t.plan_code !== sub.plan_code && (t.prices[sub.interval] ?? 0) > sub.unit_amount_minor,
      ),
    [sub],
  );
  const [count, setCount] = useState(1);
  const [plan, setPlan] = useState(kind === 'upgrade' ? higher[0]?.plan_code || '' : sub.plan_code);
  const [quote, setQuote] = useState<ChangeQuote | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState<ChangeResult | null>(null);
  const [waiting, setWaiting] = useState(false);
  //: Set once the change is paid for and applied, when the customer came from a
  //: product: they are shown the way back rather than being redirected out from
  //: under themselves, which would hide the confirmation they just paid for.
  const [done, setDone] = useState<string | null>(null);
  const max = kind === 'upgrade' ? sub.quantity : 100 - sub.quantity;

  //: `returnTo` was validated against the catalogue's product origins by the
  //: backend before it reached the page, so it is never an arbitrary URL here.
  const finish = async (message: string) => {
    if (returnTo) {
      setDone(message);
      return;
    }
    await onDone(message);
  };

  useEffect(() => {
    setQuote(null);
    setError(null);
    if (count < 1 || count > max || !plan) return;
    const t = setTimeout(() => {
      api.billing
        .quoteChange(orgId, sub.id, { kind, units: count, plan_code: kind === 'upgrade' ? plan : undefined })
        .then(setQuote)
        .catch((err) => setError(err instanceof Error ? err.message : 'Could not price this change'));
    }, 250);
    return () => clearTimeout(t);
  }, [orgId, sub.id, kind, count, plan, max]);

  const what =
    kind === 'add_units'
      ? `${units(sub.product_code, count)} added to ${sub.plan_name}`
      : `${units(sub.product_code, count)} moved to ${higher.find((t) => t.plan_code === plan)?.plan_name || plan}`;

  const confirm = async () => {
    if (!quote) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.billing.startChange(orgId, sub.id, {
        kind,
        units: count,
        plan_code: kind === 'upgrade' ? plan : undefined,
        expected_amount_minor: quote.amount_minor,
      });
      if (r.status === 'applied') await finish(`Done: ${what}.`);
      else if (r.status === 'pay') setPay(r);
      else setError('The change could not be made. Nothing was charged.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The change could not be made');
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    if (!pay?.change_id) return;
    setWaiting(true);
    for (let i = 0; i < 15; i++) {
      try {
        const r = await api.billing.refreshChange(orgId, pay.change_id);
        if (r.status === 'applied') return finish(`Payment received: ${what}.`);
        if (r.status === 'refunded' || r.status === 'cancelled') {
          setWaiting(false);
          setPay(null);
          setError('The plan changed in the meantime, so the payment was refunded. Please try again.');
          return;
        }
      } catch {
        // keep asking
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
    await finish('Payment received. The change will appear in a moment.');
  };

  const title =
    kind === 'add_units' ? `Add ${UNIT[sub.product_code]?.[1] || 'units'}` : `Upgrade ${sub.product_name} ${sub.plan_name}`;

  return (
    <Dialog open title={title} onClose={onClose} maxWidth={pay ? 560 : 460}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 'var(--text-sm)' }}>
        {done ? (
          <>
            <Alert tone="success" title="Done">
              {done}
            </Alert>
            <p style={{ color: 'var(--text-secondary)' }}>
              Your new allowance is active now. You can go straight back and carry on.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button variant="ghost" onClick={() => void onDone(done)}>
                Stay here
              </Button>
              <Button
                onClick={() => {
                  window.location.assign(returnTo!);
                }}
              >
                Back to your sites
              </Button>
            </div>
          </>
        ) : pay?.client_secret && pay.publishable_key ? (
          waiting ? (
            <Loader label="Payment received, confirming..." />
          ) : (
            <>
              <p style={{ color: 'var(--text-secondary)' }}>
                {quote?.card
                  ? 'Your saved card could not be charged without you. Pay here to apply the change.'
                  : 'Pay here to apply the change. This card is then used for renewals.'}
              </p>
              <EmbeddedPayment
                publishableKey={pay.publishable_key}
                clientSecret={pay.client_secret}
                onComplete={complete}
                onError={() => setError('The payment form could not be loaded.')}
              />
            </>
          )
        ) : (
          <>
            {kind === 'upgrade' && (
              <Select
                label="New plan"
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                options={higher.map((t) => ({
                  value: t.plan_code,
                  label: `${t.plan_name} · ${money(t.prices[sub.interval] ?? 0, sub.currency)} / ${PER[sub.interval]}${
                    t.offered ? ' (offer)' : ''
                  }`,
                }))}
              />
            )}
            <Input
              label={
                kind === 'upgrade'
                  ? `How many of your ${units(sub.product_code, sub.quantity)} move`
                  : `How many ${UNIT[sub.product_code]?.[1] || 'units'} to add`
              }
              type="number"
              min={1}
              max={max}
              value={String(count)}
              onChange={(e) => setCount(Math.max(0, Math.min(max, parseInt(e.target.value || '0', 10) || 0)))}
            />
            {error && (
              <Alert tone="danger" title="Not possible">
                {error}
              </Alert>
            )}
            {quote ? (
              <div
                style={{
                  padding: 12,
                  background: 'var(--bg-inset)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-tertiary)' }}>Charged now</span>
                  <strong>{money(quote.amount_minor, quote.currency)}</strong>
                </div>
                <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', lineHeight: 1.5 }}>
                  {kind === 'add_units'
                    ? `${money(quote.unit_amount_minor, quote.currency)} per ${UNIT[sub.product_code]?.[0] || 'unit'} before tax, for the rest of the period until ${day(quote.period_end)}.`
                    : `The price difference, ${money(quote.unit_amount_minor, quote.currency)} per ${UNIT[sub.product_code]?.[0] || 'unit'} before tax, for the rest of the period until ${day(quote.period_end)}.`}{' '}
                  {quote.discount_minor > 0 &&
                    `Your discount takes ${money(quote.discount_minor, quote.currency)} off. `}
                  Then billed with the subscription at each renewal.{' '}
                  {quote.card ? `Charged to your ${quote.card}.` : 'You will pay by card on the next step.'}
                </div>
              </div>
            ) : (
              !error && count >= 1 && <Loader label="Pricing..." />
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={confirm} disabled={!quote || busy}>
                {quote?.card ? `Pay ${quote ? money(quote.amount_minor, quote.currency) : ''}` : 'Continue'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

/** Remove units, move them to a cheaper plan or another billing period, from the next renewal. */
function ScheduleDialog({
  orgId,
  sub,
  onClose,
  onDone,
}: {
  orgId: string;
  sub: BillingSubscription;
  onClose: () => void;
  onDone: (message: string) => Promise<void>;
}) {
  const kept = sub.scheduled_quantity ?? sub.quantity;
  const [mode, setMode] = useState<'remove' | 'move'>(kept > 1 ? 'remove' : 'move');
  const [count, setCount] = useState(1);
  const [plan, setPlan] = useState(sub.plan_code);
  const targets = sub.targets;
  const target = targets.find((t) => t.plan_code === plan);
  const intervals = (Object.keys(target?.prices || {}) as PriceInterval[]).sort(
    (a, b) => ['monthly', 'quarterly', 'yearly'].indexOf(a) - ['monthly', 'quarterly', 'yearly'].indexOf(b),
  );
  const [interval, setInterval] = useState<PriceInterval>(sub.interval);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target && !target.prices[interval]) setInterval(intervals[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);

  const same = plan === sub.plan_code && interval === sub.interval;
  const removeMax = Math.max(0, kept - 1);
  const max = mode === 'remove' ? removeMax : kept;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.billing.scheduleChange(orgId, sub.id, {
        units: count,
        remove: mode === 'remove',
        plan_code: mode === 'move' ? plan : undefined,
        interval: mode === 'move' ? interval : undefined,
      });
      await onDone(`Planned for ${day(sub.current_period_end)}. Nothing changes until then.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not plan the change');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open title="Change at renewal" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 'var(--text-sm)' }}>
        <p style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          Takes effect on {day(sub.current_period_end)}, when the current period ends. The period already paid is not
          refunded. To stop the plan entirely, cancel its upcoming invoice instead.
        </p>
        <Select
          label="Change"
          value={mode}
          onChange={(e) => {
            setMode(e.target.value as 'remove' | 'move');
            setCount(1);
          }}
          options={[
            { value: 'remove', label: `Remove ${UNIT[sub.product_code]?.[1] || 'units'}`, disabled: removeMax < 1 },
            { value: 'move', label: 'Change plan or billing period' },
          ]}
        />
        {mode === 'move' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Select
              label="Plan"
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              options={targets.map((t) => ({ value: t.plan_code, label: t.plan_name + (t.offered ? ' (offer)' : '') }))}
            />
            <Select
              label="Billing"
              value={interval}
              onChange={(e) => setInterval(e.target.value as PriceInterval)}
              options={intervals.map((i) => ({
                value: i,
                label: `${INTERVAL_NAME[i]} · ${money(target?.prices[i] ?? 0, sub.currency)}`,
              }))}
            />
          </div>
        )}
        <Input
          label={`How many ${UNIT[sub.product_code]?.[1] || 'units'} (of ${kept})`}
          type="number"
          min={1}
          max={max}
          value={String(count)}
          onChange={(e) => setCount(Math.max(0, Math.min(max, parseInt(e.target.value || '0', 10) || 0)))}
        />
        {error && (
          <Alert tone="danger" title="Not possible">
            {error}
          </Alert>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy || count < 1 || count > max || (mode === 'move' && same)}>
            Plan it
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
